import React, { useEffect, useMemo, useRef } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Alert } from '@mui/material';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetOffer, useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';
import { useGetSalesforceSurcharges } from '~/hooks/apis/reservations/useGetSalesforceSurcharges';
import { useGetVendor } from '~/hooks/apis/vendors/useGetVendor';
import { useGetVendorParent } from '~/hooks/apis/vendors/useGetVendorParent';

import { isLTLEHotelOfferType } from '~/utils/offer';

import type { OfferBuilderProperty } from '../Content/PropertyInformation';

import CMChecklistWorkspace from './CMChecklistWorkspace';
import type { CampaignSchedule, SetupRuleInput } from './tabs/setup/types';

type ChecklistAccount = App.OfferAccount & {
  abn_verified__c?: boolean | string | null;
  bank_currency__c?: string | null;
  contract_template_id__c?: string | null;
  parentid?: string | null;
};

type ChecklistOffer = App.Offer & {
  cost_price_currency__c?: string | null;
  currencyisocode?: string | null;
  le_contracts_geo?: string | null;
  le_contracts_geo__c?: string | null;
  schedules?: Array<CampaignSchedule>;
};

type ChecklistOpportunity = App.OpportunityPage & {
  contract?: App.Contract & {
    le_contracts_geo__c?: string | null;
  };
};

export default function OfferBuilderCMChecklistPage() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const surfacedEvidenceErrorsRef = useRef<Set<string>>(new Set());
  const { data: offer, isLoading: isLoadingOffer, error: offerError } = useGetOffer(offerId);
  const isSupportedOfferType = !!offer?.type && isLTLEHotelOfferType(offer.type);
  const hasOfferLoadError = !isLoadingOffer && !!offerError && !offer;
  const shouldRenderChecklist = isLoadingOffer || (!!offer && isSupportedOfferType);
  const propertyId = isSupportedOfferType
    ? offer?.packages?.find((offerPackage) => offerPackage?.fk_property_id)?.fk_property_id
    : undefined;

  const {
    data: property,
    isLoading: isLoadingProperty,
    error: propertyError,
  } = useGetProperty<OfferBuilderProperty>({
    propertyId,
    enabled: isSupportedOfferType,
  });
  const {
    data: account,
    isLoading: isLoadingAccount,
    error: accountError,
  } = useGetVendor<ChecklistAccount>(offer?.vendor_account_id);
  const {
    data: vendorParent,
    isLoading: isLoadingVendorParent,
    error: vendorParentError,
  } = useGetVendorParent(offer?.vendor_account_id);
  const {
    data: opportunityRelated,
    isLoading: isLoadingOpportunityRelated,
    error: opportunityRelatedError,
  } = useGetOpportunityRelated(offerId);
  const {
    data: salesforceSurcharges = [],
    isLoading: isLoadingSurcharges,
    error: surchargesError,
  } = useGetSalesforceSurcharges(offerId ?? null);
  const opportunityId = offer?.id_salesforce_external ?? offerId;
  const {
    data: opportunity,
    isLoading: isLoadingOpportunity,
    error: opportunityError,
  } = useGetOpportunity<ChecklistOpportunity>(opportunityId, {
    enabled: isSupportedOfferType && !!offer?.id_salesforce_external,
  });
  const hotelName = property?.name || propertyId || 'Property';
  const checklistOffer = offer as ChecklistOffer | undefined;
  const setupInput = useMemo<SetupRuleInput>(
    () => ({
      account: {
        abnVerified: account?.abn_verified__c,
        bankCurrency: account?.bank_currency__c,
        contractTemplateId: account?.contract_template_id__c,
        currencyIsoCode: account?.currencyisocode,
        name: account?.name,
      },
      campaignSchedules: checklistOffer?.schedules,
      chainName: vendorParent?.parent_name ?? vendorParent?.parent_id ?? account?.parentid,
      exclusions: opportunity
        ? {
            brands: opportunity.key_commercial?.brand_not_running__c,
            countries: opportunity.key_commercial?.countries_not_to_run__c,
          }
        : undefined,
      opportunity: {
        costPriceCurrency:
          opportunity?.opportunity_info?.cost_price_currency__c ??
          checklistOffer?.cost_price_currency__c ??
          offer?.packages?.find((offerPackage) => offerPackage.cost_currency)?.cost_currency,
        currencyIsoCode: checklistOffer?.currencyisocode,
        geo:
          opportunity?.contract?.le_contracts_geo__c ??
          checklistOffer?.le_contracts_geo__c ??
          checklistOffer?.le_contracts_geo,
      },
      packageCurrencies: offer?.packages?.map((offerPackage) => offerPackage.cost_currency),
      property: {
        country: property?.geo_data?.country ?? property?.geo_data?.country_code,
        name: property?.name,
      },
      sourceErrors: {
        account: getEvidenceErrorMessage('vendor account', accountError),
        opportunity: getEvidenceErrorMessage('opportunity', opportunityError),
        parentAccount: getEvidenceErrorMessage('vendor parent account', vendorParentError),
        property: getEvidenceErrorMessage('property', propertyError),
      },
    }),
    [
      account,
      accountError,
      checklistOffer,
      offer,
      opportunity,
      opportunityError,
      property,
      propertyError,
      vendorParent,
      vendorParentError,
    ],
  );

  useEffect(() => {
    const evidenceErrors = [
      { label: 'vendor account', error: accountError },
      { label: 'vendor parent account', error: vendorParentError },
      { label: 'opportunity', error: opportunityError },
      { label: 'property', error: propertyError },
      { label: 'salesforce surcharge', error: surchargesError },
      { label: 'deal options & inclusions', error: opportunityRelatedError },
    ];

    evidenceErrors.forEach(({ label, error }) => {
      const message = getEvidenceErrorMessage(label, error);

      if (!message) {
        return;
      }

      const snackbarKey = `${label}:${message}`;
      if (surfacedEvidenceErrorsRef.current.has(snackbarKey)) {
        return;
      }

      surfacedEvidenceErrorsRef.current.add(snackbarKey);
      enqueueSnackbar(`${message}. Affected rows require approval.`, { variant: 'error' });
    });
  }, [
    accountError,
    enqueueSnackbar,
    opportunityError,
    opportunityRelatedError,
    propertyError,
    surchargesError,
    vendorParentError,
  ]);

  return (
    <>
      <Helmet>
        <title>CM Checklist | {hotelName}</title>
      </Helmet>

      {hasOfferLoadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to load offer details.
        </Alert>
      )}

      {!isLoadingOffer && offer && !isSupportedOfferType && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Offer Builder is only available for flash offers.
        </Alert>
      )}

      {shouldRenderChecklist && (
        <CMChecklistWorkspace
          blackoutDates={opportunity?.black_out_dates}
          bookByDate={offer?.book_by_date}
          campaignType={opportunity?.opportunity_info?.campaign_type__c ?? checklistOffer?.campaign_type ?? offer?.type}
          channelManager={property?.channel_manager}
          hotelName={hotelName}
          relatedInclusions={opportunityRelated?.inclusions}
          isLoadingProperty={isLoadingOffer || isLoadingProperty}
          isLoadingSetup={isLoadingOffer || isLoadingAccount || isLoadingVendorParent || isLoadingOpportunity}
          isLoadingSurcharges={
            isLoadingOffer || isLoadingSurcharges || isLoadingOpportunityRelated || isLoadingOpportunity
          }
          offer={offer}
          offerId={offerId}
          opportunity={opportunity}
          opportunityContract={opportunity?.contract}
          opportunityError={opportunityError}
          packages={offer?.packages}
          propertyId={property?.id}
          propertyError={propertyError}
          propertyHasTaxesPayableAtProperty={
            !!property?.taxes_payable_at_property ||
            property?.taxes_and_fees?.some((t) => t.payable_at_property) ||
            false
          }
          propertyRoomTypes={property?.room_types}
          relatedDeals={opportunityRelated?.deals}
          relatedExtraGuestSurcharges={opportunityRelated?.extra_guest_surcharges}
          setupInput={setupInput}
          surcharges={salesforceSurcharges}
          surchargesError={surchargesError || opportunityRelatedError}
          travelFromDate={opportunity?.key_dates?.expiry_date_travel_from__c}
          travelToDate={offer?.travel_to_date}
          vendorId={offer?.vendor_account_id}
        />
      )}
    </>
  );
}

function getEvidenceErrorMessage(label: string, error?: unknown) {
  if (!error) {
    return undefined;
  }

  return `Could not load ${label} evidence${getErrorStatus(error)}`;
}

function getErrorStatus(error: unknown) {
  const status = getErrorStatusValue(error);

  if (status !== undefined) {
    return ` (${status})`;
  }

  return '';
}

function getErrorStatusValue(error: unknown) {
  if (typeof error === 'object' && error && 'status' in error) {
    const { status } = error;

    if (typeof status === 'number') {
      return status;
    }

    if (typeof status === 'string') {
      const parsedStatus = Number(status);
      return Number.isNaN(parsedStatus) ? undefined : parsedStatus;
    }
  }

  return undefined;
}
